import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import moment from "moment";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ProjectStatus,
  useGetProjectsQuery,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../../common/editing/save-coordinator";
import { DateTimeSelector } from "../../../../../../common/input/DateTimeSelectors/DateTimeSelector";
import { TipTap } from "../../../../../../common/input/TipTap/TipTap";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { useSub } from "../../../../../../common/subscription/useSub";
import { ProjectsSelector } from "../../../../../projects/ProjectsSelector";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const DetailsStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const sub = useSub();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const { data: projectsData } = useGetProjectsQuery(
    sub.hasProjectAccess
      ? { status: [ProjectStatus.NotStarted, ProjectStatus.Ongoing].join(","), limit: 100 }
      : skipToken,
  );

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const referenceIdError = fieldError("eventData.referenceId");
  const deadlineError = fieldError("eventData.bidSubmissionDeadline");

  const hasDeadline = state.eventData.bidSubmissionDeadline != null;
  useEffect(() => {
    if (!hasDeadline) {
      updateField({
        eventData: { bidSubmissionDeadline: moment().endOf("day").set("ms", 0).set("s", 0).toISOString() },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedProjects = (state.eventData.projects ?? []).map((projectId) => {
    const project = projectsData?.projects.find((p) => p.id === projectId);
    return {
      label: project ? `${project.externalId ? `${project.externalId} - ` : ""}${project.name}` : projectId,
      value: projectId,
    };
  });

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormControl mb="6">
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Description")}</FormLabel>
            {isSaving("eventData.description") && <Spinner size="xs" />}
          </Flex>
          <TipTap
            content={state.eventData.description ?? ""}
            onUpdate={(value) => updateField({ eventData: { description: value } })}
            editable={true}
            debounceWait={300}
          />
          <FormHelperText>{t("Describe the scope and requirements of this procurement")}</FormHelperText>
        </FormControl>

        <FormControl mb="6" isInvalid={!!referenceIdError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Reference id")}</FormLabel>
            {isSaving("eventData.referenceId") && <Spinner size="xs" />}
          </Flex>
          <Input
            type="text"
            value={state.eventData.referenceId ?? ""}
            onChange={(e) => {
              const trimmed = e.target.value.trim();
              updateField({ eventData: { referenceId: trimmed.length === 0 ? null : e.target.value } }, { debounceMs: 300 });
            }}
          />
          {referenceIdError ? (
            <FormErrorMessage>{formatFieldError(referenceIdError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("Optional internal reference for this procurement")}</FormHelperText>
          )}
        </FormControl>

        <FormControl mb="6" isInvalid={!!deadlineError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Deadline")}</FormLabel>
            {isSaving("eventData.bidSubmissionDeadline") && <Spinner size="xs" />}
          </Flex>
          <DateTimeSelector
            date={state.eventData.bidSubmissionDeadline ?? null}
            onChange={(date) =>
              updateField({
                eventData: { bidSubmissionDeadline: date ?? null, bidSubmissionDeadlineConfirmed: date != null },
              })
            }
          />
          {deadlineError ? (
            <FormErrorMessage>{formatFieldError(deadlineError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("The deadline for suppliers to submit their bids")}</FormHelperText>
          )}
        </FormControl>

        {sub.hasProjectAccess && (
          <FormControl>
            <FormLabel>{t("Projects")}</FormLabel>
            <ProjectsSelector
              selectedProjects={selectedProjects}
              onChange={(values) => updateField({ eventData: { projects: values.map((v) => v.value) } })}
            />
            <FormHelperText>{t("Select one or more projects the sourcing event is connected to")}.</FormHelperText>
          </FormControl>
        )}
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
